import { useCallback, useContext, useEffect, useSyncExternalStore } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  useStore,
} from '@ea-lab/reactive-json'

/**
 * UrlSync — miroir « clé du store → paramètre d'URL de même nom ».
 *
 * POURQUOI DU JS (et rien de plus). C'est la pièce IRRÉDUCTIBLE d'un filtre
 * partagé entre widgets : écrire dans la query string est une API du navigateur,
 * qu'aucun rjBuild n'exprime. Tout le reste reste du YAML — les contrôles sont
 * les champs existants (`SelectField`, `FacetList`), la lecture de l'URL est déjà
 * assurée par `overlayUrlParams` (cf. `BuilderBlockMount`). Ce composant ne rend
 * donc RIEN et ne connaît aucun filtre : il ne fait que refléter des clés.
 *
 * POURQUOI L'URL EST LE BON CANAL. Chaque cellule d'une grille de tableau de bord
 * est montée dans son PROPRE `ReactiveJsonRoot` (`BuilderBlockMount`) : le `~~.`
 * d'un widget ne voit jamais le store d'un voisin, il n'y a donc aucun état
 * partagé entre widgets. Mais toutes les cellules lisent le MÊME
 * `location.search` (l'appli n'a qu'un `BrowserRouter`), et `overlayUrlParams`
 * surcharge une clé de l'URL uniquement si le bloc la DÉCLARE dans son `data:`.
 * L'URL est donc un canal de diffusion déjà câblé, avec un opt-in par widget :
 * un widget « écoute » un filtre en déclarant la clé, les autres l'ignorent.
 *
 * Le pendant côté récepteur : `filters:` de `ChartLoader` accepte un scalaire
 * comme un tableau, et sait déjà en faire `filter[champ]=` ou `filter[champ][]=`
 * répétés (clause `terms`, un OU) — un multi-select ne demande donc aucun code
 * supplémentaire, seulement un défaut déclaré en tableau (`provider: []`).
 *
 * ABONNEMENT AU MAGASIN — le piège qui a mordu ici. Un composant React monté
 * comme plugin lit le store en accès NON RÉACTIF : avec `evaluateTemplateValue`
 * seul, ce composant se rendait une fois puis restait figé, et cocher une facette
 * ne mettait jamais l'URL à jour — sans la moindre erreur. On s'abonne donc au
 * `DataStore` comme le fait le moteur lui-même (`View` : `useSyncExternalStore` +
 * `store.subscribe(path)`). L'instantané est une CHAÎNE (comparaison par valeur) :
 * un objet neuf à chaque appel ferait boucler `useSyncExternalStore`.
 *
 * Usage YAML (dans le bloc qui PORTE les contrôles) :
 *   data:
 *     provider: []              # multi-valeur (tableau) ← seedé depuis l'URL
 *     period_days: ""           # valeur simple
 *   renderView:
 *     - type: UrlSync
 *       keys: [provider, period_days]
 *     - type: FacetList         # écrit ~~.provider (tableau)
 *       …
 *
 * Sens de circulation, et pourquoi il n'y a pas de boucle :
 *   contrôle → store → (ici) URL → re-seed de TOUS les blocs → store.
 * La comparaison porte sur les VALEURS des clés suivies (pas sur la chaîne de
 * requête entière, dont l'ordre change à la réécriture) : une fois l'URL à jour,
 * les deux signatures concordent et plus rien n'est écrit.
 *
 * `replace: true` : un filtre n'est pas une étape de navigation — sans ça, cocher
 * quatre cases empilerait quatre entrées d'historique à défaire une par une.
 */

/**
 * Normalise une valeur de store en liste de chaînes destinée à l'URL.
 *
 * Vide = absent : `null`, `''` et `[]` ne produisent aucun paramètre (un filtre
 * non renseigné ne doit pas restreindre les widgets — même règle que le `isEmpty`
 * de `ChartLoader`). Une entrée peut être une enveloppe `{ value, label }` (ce
 * que stockent les pickers de référentiel comme `FuzzySelectField`) : seule la
 * `value` part dans l'URL, comme côté requête.
 */
const asUrlList = (value) => {
  if (value == null) return []
  const list = Array.isArray(value) ? value : [value]
  return list
    .map((v) => (v && typeof v === 'object' && !Array.isArray(v) ? v.value : v))
    .filter((v) => v != null && v !== '')
    .map(String)
}

export const UrlSync = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const store = useStore()

  const rawKeys = evaluateTemplateValue({
    valueToEvaluate: props.keys,
    globalDataContext,
    templateContext,
  })
  const keys = (Array.isArray(rawKeys) ? rawKeys : [rawKeys])
    .filter((k) => typeof k === 'string' && k !== '')
  // Signature stable des clés : les dépendances des hooks ci-dessous ne peuvent
  // pas porter sur le tableau lui-même (neuf à chaque rendu).
  const keysSig = keys.join(',')

  // Les clés suivies sont à la RACINE du store du bloc (`~~.<clé>`), soit le
  // chemin `<clé>` côté DataStore (chemins pointés).
  const subscribe = useCallback((onChange) => {
    const unsubs = keysSig ? keysSig.split(',').map((k) => store.subscribe(k, onChange)) : []
    return () => unsubs.forEach((u) => u())
  }, [store, keysSig])

  const getSnapshot = useCallback(
    () => JSON.stringify(keysSig ? keysSig.split(',').map((k) => asUrlList(store.get(k))) : []),
    [store, keysSig],
  )

  const desiredSig = useSyncExternalStore(subscribe, getSnapshot)

  const [searchParams, setSearchParams] = useSearchParams()
  const currentSig = JSON.stringify(keys.map((k) => searchParams.getAll(k)))

  useEffect(() => {
    if (currentSig === desiredSig) return
    const desired = JSON.parse(desiredSig)
    // Mise à jour fonctionnelle : on repart des paramètres COURANTS pour ne pas
    // perdre ceux qu'on ne gère pas (`?edit=1`, `?id=…`, un filtre d'un autre
    // UrlSync).
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      keysSig.split(',').forEach((key, i) => {
        next.delete(key)
        ;(desired[i] ?? []).forEach((v) => next.append(key, v))
      })
      return next
    }, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSig, desiredSig, keysSig])

  return null
}
